import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, type RedlineCreateResult, type RedlinePairProposal } from "../lib/api";
import { useAuth } from "../lib/auth";
import {
  MAX_NOTES_CHARS,
  REVIEW_SCOPE_PRESETS,
  REVIEW_SECTION_LABEL,
  REVIEW_TYPE_LABEL,
  SKIPPABLE_SECTIONS,
  type Attachment,
  type AttachmentKind,
  type Deal,
  type DocumentVersion,
  type Review,
  type ReviewScopePreset,
  type ReviewSectionKey,
  type ReviewStage,
  type ReviewType,
} from "@docreview/shared";

const STAGES: { value: ReviewStage; label: string }[] = [
  { value: "first_draft", label: "First draft" },
  { value: "redline_response", label: "Redline response" },
  { value: "final_check", label: "Final check" },
];

const REVIEW_TYPES: { value: ReviewType; label: string; description: string }[] = [
  {
    value: "document_review",
    label: REVIEW_TYPE_LABEL.document_review,
    description: "Full four-section review of one document version against the TS / Intake.",
  },
  {
    value: "redline_review",
    label: REVIEW_TYPE_LABEL.redline_review,
    description:
      "Diff-focused review comparing a new version against a prior version (e.g., counterparty redline).",
  },
  {
    value: "standalone_review",
    label: REVIEW_TYPE_LABEL.standalone_review,
    description:
      "Review a single document on its own (e.g. a side letter) — internal consistency, defined-term gaps, and loopholes. No term sheet / intake comparison required.",
  },
];

export default function DealPage() {
  const { id } = useParams<{ id: string }>();
  const dealId = id!;
  const { user } = useAuth();
  const isAdmin = useAuth().isAdmin === true;
  const nav = useNavigate();

  const [deal, setDeal] = useState<Deal | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [showNewReview, setShowNewReview] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [showDeleteDeal, setShowDeleteDeal] = useState(false);

  async function refresh() {
    const [d, a, v, r] = await Promise.all([
      api.getDeal(dealId),
      api.listAttachments(dealId),
      api.listVersions(dealId),
      api.listReviews(dealId),
    ]);
    setDeal(d.deal);
    setAttachments(a.attachments);
    setVersions(v.versions);
    setReviews(r.reviews);
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  if (!deal) return <div className="p-8 text-slate-500">Loading…</div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      <div className="mb-4">
        <Link
          to="/"
          className="text-sm text-slate-500 hover:text-ink inline-flex items-center gap-1"
        >
          <span>←</span> Deals
        </Link>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
        <aside className="space-y-4">
          <div className="card p-4">
            <h1 className="font-semibold text-lg">
              {deal.visibility === "private" && (
                <span className="mr-1.5 text-slate-400" title="Private — only you can see this deal">
                  &#128274;
                </span>
              )}
              {deal.title}
            </h1>
            <div className="text-xs text-slate-500 mt-1">
              {deal.dealType} · created by {deal.createdByName ?? deal.createdByEmail}
            </div>
            {isAdmin && (
              <div className="text-xs text-slate-500 mt-3 pt-3 border-t border-slate-100 space-y-2">
                <div>Total across all reviews: ${deal.totalCostUsd.toFixed(4)}</div>
                <div>
                  <button
                    className="text-red-600 hover:text-red-800 hover:underline"
                    onClick={() => setShowDeleteDeal(true)}
                    title="Permanently delete this deal and everything under it"
                  >
                    Delete deal
                  </button>
                </div>
              </div>
            )}
          </div>

          <NotesPanel
            deal={deal}
            editing={editingNotes}
            onStartEdit={() => setEditingNotes(true)}
            onSaved={(updated) => {
              setDeal(updated);
              setEditingNotes(false);
            }}
            onCancel={() => setEditingNotes(false)}
          />

          <DocumentsPanel
            dealId={dealId}
            attachments={attachments}
            versions={versions}
            onChange={refresh}
          />
        </aside>

        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-semibold">Reviews</h2>
            <button className="btn-primary" onClick={() => setShowNewReview(true)}>
              New review
            </button>
          </div>
          {reviews.length === 0 ? (
            <div className="card p-8 text-center text-slate-500">
              No reviews yet. Click "New review" to start one on this deal.
            </div>
          ) : (
            <ul className="grid gap-2">
              {reviews.map((r) => (
                <li key={r.id} className="relative">
                  <Link
                    to={`/deals/${dealId}/reviews/${r.id}`}
                    className="card p-4 flex items-center justify-between hover:border-slate-400 hover:shadow transition"
                  >
                    <div className="min-w-0">
                      <div className="font-medium truncate">{r.title}</div>
                      <div className="text-xs text-slate-500 mt-0.5">
                        {STAGES.find((s) => s.value === r.reviewStage)?.label} · created by{" "}
                        {r.createdByName ?? r.createdByEmail}
                      </div>
                    </div>
                    <div className="text-xs text-slate-500 text-right shrink-0 ml-4 pr-14">
                      {isAdmin && <div>${r.totalCostUsd.toFixed(4)}</div>}
                      <div>{new Date(r.updatedAt).toLocaleDateString()}</div>
                    </div>
                  </Link>
                  {isAdmin && (
                    <button
                      className="absolute top-2 right-2 text-xs text-red-600 hover:text-red-800 px-2 py-1 rounded hover:bg-red-50"
                      title="Delete this review (admin)"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (
                          !window.confirm(
                            `Delete "${r.title}"? This removes all messages and checklist items for this review. Cannot be undone.`,
                          )
                        )
                          return;
                        try {
                          await api.deleteReview(dealId, r.id);
                          await refresh();
                        } catch (err) {
                          window.alert(
                            `Couldn't delete: ${err instanceof Error ? err.message : String(err)}`,
                          );
                        }
                      }}
                    >
                      delete
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {showNewReview && (
        <NewReviewModal
          dealId={dealId}
          versions={versions}
          attachments={attachments}
          onClose={() => setShowNewReview(false)}
          onCreated={(r) => nav(`/deals/${dealId}/reviews/${r.id}`)}
        />
      )}
      {showDeleteDeal && (
        <DeleteDealModal
          deal={deal}
          onClose={() => setShowDeleteDeal(false)}
          onDeleted={() => nav("/")}
        />
      )}
    </div>
  );
}

function NotesPanel({
  deal,
  editing,
  onStartEdit,
  onSaved,
  onCancel,
}: {
  deal: Deal;
  editing: boolean;
  onStartEdit: () => void;
  onSaved: (d: Deal) => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState(deal.notes);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(deal.notes);
  }, [deal.notes, editing]);

  if (!editing) {
    return (
      <div className="card p-4">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold text-sm">Notes</h3>
          <button className="text-xs text-ink hover:underline" onClick={onStartEdit}>
            edit
          </button>
        </div>
        {deal.notes && deal.notes.trim().length > 0 ? (
          <div className="text-sm text-slate-700 whitespace-pre-wrap">{deal.notes}</div>
        ) : (
          <div className="text-xs text-slate-400 italic">No notes for this deal.</div>
        )}
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h3 className="font-semibold text-sm mb-2">Notes</h3>
      <textarea
        className="input resize-none"
        rows={6}
        maxLength={MAX_NOTES_CHARS}
        value={draft}
        onChange={(e) => setDraft(e.target.value.slice(0, MAX_NOTES_CHARS))}
      />
      <div className="text-right text-xs text-slate-400 mt-1">
        {draft.length} / {MAX_NOTES_CHARS}
      </div>
      <div className="flex justify-end gap-2 mt-2">
        <button className="btn-secondary text-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn-primary text-sm"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              const { deal: updated } = await api.patchDeal(deal.id, { notes: draft });
              onSaved(updated);
            } finally {
              setBusy(false);
            }
          }}
        >
          Save
        </button>
      </div>
    </div>
  );
}

type UploadStatus =
  | { state: "uploading"; filename: string }
  | { state: "error"; filename: string; message: string };

export function DocumentsPanel({
  dealId,
  attachments,
  versions,
  onChange,
  variant = "edit",
}: {
  dealId: string;
  attachments: Attachment[];
  versions: DocumentVersion[];
  onChange: () => void;
  variant?: "edit" | "read";
}) {
  const [pending, setPending] = useState<UploadStatus[]>([]);
  const [showNewVersion, setShowNewVersion] = useState(false);
  const [showRedlineCreator, setShowRedlineCreator] = useState(false);

  async function uploadMany(
    files: File[],
    kind: AttachmentKind,
    versionId?: string,
  ) {
    if (files.length === 0) return;
    const accepted = files.filter((f) =>
      /\.(docx|pdf)$/i.test(f.name) ||
      f.type === "application/pdf" ||
      f.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    );
    const rejected = files.filter((f) => !accepted.includes(f));
    setPending((prev) => [
      ...prev,
      ...accepted.map((f) => ({ state: "uploading" as const, filename: f.name })),
      ...rejected.map((f) => ({
        state: "error" as const,
        filename: f.name,
        message: "Only .docx and .pdf are supported.",
      })),
    ]);
    await Promise.all(
      accepted.map(async (f) => {
        try {
          await api.uploadAttachment(dealId, f, kind, versionId);
          setPending((prev) => prev.filter((p) => p.filename !== f.name));
        } catch (e) {
          const message = e instanceof Error ? e.message : String(e);
          setPending((prev) =>
            prev.map((p) =>
              p.filename === f.name && p.state === "uploading"
                ? { state: "error", filename: f.name, message }
                : p,
            ),
          );
        }
      }),
    );
    onChange();
  }

  // TS and Intake stay at the deal level (no versionId). Everything
  // else lives inside a DocumentVersion; legacy uploads without a
  // versionId are surfaced separately so the team can see them.
  const tsItems = attachments.filter((a) => a.kind === "term_sheet");
  const intakeItems = attachments.filter((a) => a.kind === "intake_sheet");
  const versionedAttachments = useMemo(
    () =>
      attachments.filter(
        (a) =>
          (a.kind === "deal_doc" || a.kind === "revised_deal_doc") && !!a.versionId,
      ),
    [attachments],
  );
  const unversionedAttachments = useMemo(
    () =>
      attachments.filter(
        (a) =>
          (a.kind === "deal_doc" || a.kind === "revised_deal_doc") && !a.versionId,
      ),
    [attachments],
  );

  async function handleDelete(id: string) {
    await api.deleteAttachment(id);
    onChange();
  }

  return (
    <div className="card p-4">
      <h3 className="font-semibold mb-2">Documents</h3>
      <div className="space-y-3 text-sm">
        <Group
          title="Term sheet"
          kind="term_sheet"
          items={tsItems}
          variant={variant}
          dealId={dealId}
          onDelete={handleDelete}
          onDrop={(files) => uploadMany(files, "term_sheet")}
        />
        <Group
          title="Intake sheet"
          kind="intake_sheet"
          items={intakeItems}
          variant={variant}
          dealId={dealId}
          onDelete={handleDelete}
          onDrop={(files) => uploadMany(files, "intake_sheet")}
        />

        <div className="pt-3 border-t border-slate-100">
          <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
            Document versions
          </div>
          {versions.length === 0 && unversionedAttachments.length === 0 ? (
            <div className="text-xs text-slate-400 italic">
              No document versions yet. Create one to upload deal documents into.
            </div>
          ) : (
            <ul className="space-y-3">
              {versions.map((v) => (
                <VersionCard
                  key={v.id}
                  dealId={dealId}
                  version={v}
                  files={versionedAttachments.filter((a) => a.versionId === v.id)}
                  variant={variant}
                  onDelete={handleDelete}
                  onUpload={(files) => uploadMany(files, "deal_doc", v.id)}
                  onRenamed={onChange}
                />
              ))}
              {unversionedAttachments.length > 0 && (
                <LegacyVersionCard
                  dealId={dealId}
                  files={unversionedAttachments}
                  variant={variant}
                  onDelete={handleDelete}
                />
              )}
            </ul>
          )}
          {variant === "edit" && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                className="btn-secondary text-sm"
                onClick={() => setShowNewVersion(true)}
              >
                + New version
              </button>
              {versions.length >= 2 && (
                <button
                  className="btn-secondary text-sm"
                  onClick={() => setShowRedlineCreator(true)}
                  title="Compare the Word docs of two versions and generate tracked-changes redlines"
                >
                  ⇄ Create redlines
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {variant === "edit" && (
        <div className="mt-3 flex flex-wrap gap-2">
          <FileButton kind="term_sheet" label="Term sheet" onPick={uploadMany} />
          <FileButton kind="intake_sheet" label="Intake sheet" onPick={uploadMany} />
        </div>
      )}

      {pending.length > 0 && (
        <ul className="mt-3 space-y-1">
          {pending.map((p) => (
            <li
              key={p.filename}
              className={`text-xs flex items-center gap-2 ${
                p.state === "error" ? "text-red-600" : "text-slate-500"
              }`}
            >
              {p.state === "uploading" ? (
                <>
                  <Spinner /> <span className="truncate">{p.filename}</span>
                </>
              ) : (
                <>
                  <span>⚠</span>
                  <span className="truncate">
                    {p.filename} — {p.message}
                  </span>
                  <button
                    className="ml-auto text-slate-400 hover:text-slate-700"
                    onClick={() =>
                      setPending((prev) => prev.filter((q) => q.filename !== p.filename))
                    }
                  >
                    ×
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}

      {showNewVersion && (
        <NewVersionModal
          dealId={dealId}
          onClose={() => setShowNewVersion(false)}
          onCreated={() => {
            setShowNewVersion(false);
            onChange();
          }}
        />
      )}

      {showRedlineCreator && (
        <RedlineCreatorModal
          dealId={dealId}
          versions={versions}
          onClose={() => setShowRedlineCreator(false)}
          onCreated={() => {
            setShowRedlineCreator(false);
            onChange();
          }}
        />
      )}
    </div>
  );
}

/**
 * Redline Creator: pick two versions ("what we sent" vs "what they
 * returned"), confirm the proposed document pairings, and generate Word
 * tracked-changes redlines into a new version. Pairing confirmation is
 * mandatory — a mis-paired compare produces a garbage redline.
 */
function RedlineCreatorModal({
  dealId,
  versions,
  onClose,
  onCreated,
}: {
  dealId: string;
  versions: DocumentVersion[];
  onClose: () => void;
  onCreated: () => void;
}) {
  const sorted = useMemo(() => [...versions].sort((a, b) => a.order - b.order), [versions]);
  const [oursVersionId, setOursVersionId] = useState(
    sorted.length >= 2 ? sorted[sorted.length - 2]!.id : "",
  );
  const [theirsVersionId, setTheirsVersionId] = useState(
    sorted.length >= 1 ? sorted[sorted.length - 1]!.id : "",
  );
  const [author, setAuthor] = useState("");
  const [proposal, setProposal] = useState<RedlinePairProposal | null>(null);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [manualPairs, setManualPairs] = useState<
    { oursId: string; oursFilename: string; theirsId: string; theirsFilename: string }[]
  >([]);
  const [manualOurs, setManualOurs] = useState("");
  const [manualTheirs, setManualTheirs] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [results, setResults] = useState<RedlineCreateResult[] | null>(null);

  const pairKey = (oursId: string, theirsId: string) => `${oursId}::${theirsId}`;

  async function propose() {
    setBusy(true);
    setErr(null);
    setProposal(null);
    setManualPairs([]);
    try {
      const p = await api.proposeRedlinePairs(dealId, oursVersionId, theirsVersionId);
      setProposal(p);
      setChecked(Object.fromEntries(p.pairs.map((x) => [pairKey(x.oursId, x.theirsId), true])));
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const confirmedPairs = useMemo(() => {
    if (!proposal) return [];
    const auto = proposal.pairs
      .filter((p) => checked[pairKey(p.oursId, p.theirsId)])
      .map((p) => ({ oursId: p.oursId, theirsId: p.theirsId }));
    return [...auto, ...manualPairs.map((p) => ({ oursId: p.oursId, theirsId: p.theirsId }))];
  }, [proposal, checked, manualPairs]);

  // Files still available for a manual pairing: not used by a checked
  // auto-pair and not already manually paired.
  const usedOurs = new Set(confirmedPairs.map((p) => p.oursId));
  const usedTheirs = new Set(confirmedPairs.map((p) => p.theirsId));
  const freeOurs = proposal?.oursDocx.filter((f) => !usedOurs.has(f.id)) ?? [];
  const freeTheirs = proposal?.theirsDocx.filter((f) => !usedTheirs.has(f.id)) ?? [];

  async function run() {
    setBusy(true);
    setErr(null);
    try {
      const resp = await api.createRedlines(dealId, {
        oursVersionId,
        theirsVersionId,
        ...(author.trim() ? { author: author.trim() } : {}),
        pairs: confirmedPairs,
      });
      setResults(resp.results);
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      if (msg.includes("all_compares_failed")) {
        // Surface the first engine error — an opaque "couldn't process"
        // hides actionable detail (as the ICU incident proved).
        let detail = "";
        try {
          const parsed = JSON.parse(msg.slice(msg.indexOf("{"))) as {
            results?: { error?: string }[];
          };
          detail = parsed.results?.find((r) => r.error)?.error ?? "";
        } catch {
          // response wasn't JSON — keep the generic message
        }
        setErr(
          `The compare engine couldn't process any of these documents.${detail ? ` First error: ${detail}` : ""}`,
        );
      } else {
        setErr(msg);
      }
    } finally {
      setBusy(false);
    }
  }

  const versionLabel = (id: string) => sorted.find((v) => v.id === id)?.label ?? id;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="card w-full max-w-2xl p-6 max-h-[85vh] overflow-y-auto">
        <h2 className="font-semibold text-lg mb-2">Create redlines</h2>

        {results ? (
          <>
            <p className="text-sm text-slate-600 mb-3">
              Generated redlines were saved into a new version. Word shows every change as a
              tracked change under the author you picked — open a file and use Accept/Reject
              as usual.
            </p>
            <ul className="space-y-1 mb-4 text-sm">
              {results.map((r) => (
                <li key={pairKey(r.oursId, r.theirsId)} className="flex items-center gap-2">
                  {r.ok ? (
                    <>
                      <span className="text-green-600">✓</span>
                      <span className="truncate">{r.attachment?.filename ?? r.theirsFilename}</span>
                      <span className="text-xs text-slate-400 ml-auto shrink-0">
                        {r.revisionCount === 0
                          ? "identical — no redline needed"
                          : `${r.revisionCount} revision${r.revisionCount === 1 ? "" : "s"}`}
                      </span>
                    </>
                  ) : (
                    <>
                      <span className="text-red-600">⚠</span>
                      <span className="text-red-600 truncate">{r.error}</span>
                    </>
                  )}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <button className="btn-primary" onClick={onCreated}>
                Done
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="text-sm text-slate-600 mb-4">
              Compare the Word documents of two versions and generate real tracked-changes
              redlines — like Word's Compare Documents, but for the whole set at once. PDFs
              are skipped.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-sm font-medium mb-1">Original ("what we sent")</label>
                <select
                  className="input"
                  value={oursVersionId}
                  onChange={(e) => {
                    setOursVersionId(e.target.value);
                    setProposal(null);
                    setErr(null);
                  }}
                >
                  {sorted.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.order} — {v.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Revised ("what they returned")
                </label>
                <select
                  className="input"
                  value={theirsVersionId}
                  onChange={(e) => {
                    setTheirsVersionId(e.target.value);
                    setProposal(null);
                    setErr(null);
                  }}
                >
                  {sorted.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.order} — {v.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <label className="block text-sm font-medium mb-1">
              Author for the tracked changes
            </label>
            <input
              className="input mb-4"
              maxLength={80}
              value={author}
              onChange={(e) => setAuthor(e.target.value)}
              placeholder="Defaults to your name"
            />

            {proposal === null ? (
              <div className="flex justify-end gap-2">
                <button className="btn-secondary" onClick={onClose}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  disabled={busy || !oursVersionId || !theirsVersionId || oursVersionId === theirsVersionId}
                  onClick={propose}
                >
                  {busy ? "Matching…" : "Match documents"}
                </button>
              </div>
            ) : (
              <>
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-2">
                  Confirm pairings — {versionLabel(oursVersionId)} → {versionLabel(theirsVersionId)}
                </div>
                {proposal.pairs.length === 0 && manualPairs.length === 0 && (
                  <div className="text-sm text-slate-500 mb-2">
                    No filenames matched automatically. Pair documents manually below.
                  </div>
                )}
                <ul className="space-y-1 mb-3">
                  {proposal.pairs.map((p) => (
                    <li key={pairKey(p.oursId, p.theirsId)} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={!!checked[pairKey(p.oursId, p.theirsId)]}
                        onChange={(e) =>
                          setChecked((prev) => ({
                            ...prev,
                            [pairKey(p.oursId, p.theirsId)]: e.target.checked,
                          }))
                        }
                      />
                      <span className="truncate">{p.oursFilename}</span>
                      <span className="text-slate-400 shrink-0">→</span>
                      <span className="truncate">{p.theirsFilename}</span>
                    </li>
                  ))}
                  {manualPairs.map((p, i) => (
                    <li key={pairKey(p.oursId, p.theirsId)} className="flex items-center gap-2 text-sm">
                      <button
                        className="text-slate-400 hover:text-red-600"
                        title="Remove pair"
                        onClick={() =>
                          setManualPairs((prev) => prev.filter((_, j) => j !== i))
                        }
                      >
                        ×
                      </button>
                      <span className="truncate">{p.oursFilename}</span>
                      <span className="text-slate-400 shrink-0">→</span>
                      <span className="truncate">{p.theirsFilename}</span>
                    </li>
                  ))}
                </ul>
                {freeOurs.length > 0 && freeTheirs.length > 0 && (
                  <div className="flex items-center gap-2 mb-3 text-sm">
                    <select
                      className="input flex-1"
                      value={manualOurs}
                      onChange={(e) => setManualOurs(e.target.value)}
                    >
                      <option value="">Unmatched original…</option>
                      {freeOurs.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.filename}
                        </option>
                      ))}
                    </select>
                    <span className="text-slate-400">→</span>
                    <select
                      className="input flex-1"
                      value={manualTheirs}
                      onChange={(e) => setManualTheirs(e.target.value)}
                    >
                      <option value="">Unmatched revised…</option>
                      {freeTheirs.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.filename}
                        </option>
                      ))}
                    </select>
                    <button
                      className="btn-secondary text-sm shrink-0"
                      disabled={!manualOurs || !manualTheirs}
                      onClick={() => {
                        const o = freeOurs.find((f) => f.id === manualOurs)!;
                        const t = freeTheirs.find((f) => f.id === manualTheirs)!;
                        setManualPairs((prev) => [
                          ...prev,
                          {
                            oursId: o.id,
                            oursFilename: o.filename,
                            theirsId: t.id,
                            theirsFilename: t.filename,
                          },
                        ]);
                        setManualOurs("");
                        setManualTheirs("");
                      }}
                    >
                      + Pair
                    </button>
                  </div>
                )}
                {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
                {busy && (
                  <div className="text-xs text-slate-400 mb-2">
                    Comparing on the server — closing this window won't stop it.
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <button className="btn-secondary" onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className="btn-primary"
                    disabled={busy || confirmedPairs.length === 0}
                    onClick={run}
                  >
                    {busy
                      ? "Comparing…"
                      : `Create ${confirmedPairs.length} redline${confirmedPairs.length === 1 ? "" : "s"}`}
                  </button>
                </div>
              </>
            )}
            {proposal === null && err && <div className="text-sm text-red-600 mt-3">{err}</div>}
          </>
        )}
      </div>
    </div>
  );
}

function NewVersionModal({
  dealId,
  onClose,
  onCreated,
}: {
  dealId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="card w-full max-w-md p-6">
        <h2 className="font-semibold text-lg mb-2">New version</h2>
        <p className="text-sm text-slate-600 mb-4">
          Group the documents you're about to upload under a labeled version. Examples:
          "Original documents", "Post-review v1", "Company redline 5/27".
        </p>
        <label className="block text-sm font-medium mb-1">Label</label>
        <input
          className="input mb-3"
          maxLength={80}
          value={label}
          onChange={(e) => setLabel(e.target.value.slice(0, 80))}
          placeholder="Post-review v1"
          autoFocus
        />
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={busy || label.trim().length === 0}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                await api.createVersion(dealId, label.trim());
                onCreated();
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VersionCard({
  dealId,
  version,
  files,
  variant,
  onDelete,
  onUpload,
  onRenamed,
}: {
  dealId: string;
  version: DocumentVersion;
  files: Attachment[];
  variant: "edit" | "read";
  onDelete: (id: string) => void;
  onUpload: (files: File[]) => void;
  onRenamed: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState(version.label);
  const [dragOver, setDragOver] = useState(false);

  // Warn inline if the user picks a file whose name+size collides with
  // something already in the version (helps catch accidental dupes).
  const existingKeys = useMemo(
    () => new Set(files.map((f) => `${f.filename}::${f.sizeBytes}`)),
    [files],
  );

  async function downloadAll() {
    setDownloading(true);
    try {
      await api.downloadAttachmentsZip(dealId, { versionId: version.id });
    } catch (e) {
      window.alert(`Couldn't download: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDownloading(false);
    }
  }

  function pickFiles(picked: File[]) {
    const dupes = picked.filter((f) => existingKeys.has(`${f.name}::${f.size}`));
    if (dupes.length > 0) {
      const ok = window.confirm(
        `${dupes.length} file${dupes.length === 1 ? "" : "s"} already in this version with the same name + size:\n\n${dupes.map((f) => `• ${f.name}`).join("\n")}\n\nUpload again anyway?`,
      );
      if (!ok) return;
    }
    onUpload(picked);
  }

  return (
    <li
      className={`rounded-md border border-slate-200 p-3 transition ${
        dragOver ? "ring-2 ring-ink/40 bg-slate-50" : ""
      }`}
      onDragEnter={(e) => {
        if (variant !== "edit") return;
        e.preventDefault();
        if (e.dataTransfer?.types.includes("Files")) setDragOver(true);
      }}
      onDragOver={(e) => {
        if (variant !== "edit") return;
        e.preventDefault();
        if (e.dataTransfer?.types.includes("Files")) setDragOver(true);
      }}
      onDragLeave={(e) => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setDragOver(false);
      }}
      onDrop={(e) => {
        if (variant !== "edit") return;
        e.preventDefault();
        setDragOver(false);
        const picked = Array.from(e.dataTransfer?.files ?? []);
        if (picked.length > 0) pickFiles(picked);
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="min-w-0 flex-1">
          {renaming ? (
            <div className="flex items-center gap-1">
              <input
                className="input text-sm flex-1"
                value={renameDraft}
                maxLength={80}
                onChange={(e) => setRenameDraft(e.target.value.slice(0, 80))}
                autoFocus
              />
              <button
                className="text-xs text-ink font-medium hover:underline"
                onClick={async () => {
                  const trimmed = renameDraft.trim();
                  if (trimmed.length === 0 || trimmed === version.label) {
                    setRenaming(false);
                    return;
                  }
                  await api.renameVersion(dealId, version.id, trimmed);
                  setRenaming(false);
                  onRenamed();
                }}
              >
                save
              </button>
              <button
                className="text-xs text-slate-400 hover:text-slate-700"
                onClick={() => {
                  setRenameDraft(version.label);
                  setRenaming(false);
                }}
              >
                cancel
              </button>
            </div>
          ) : (
            <div className="font-medium text-sm truncate">{version.label}</div>
          )}
          <div className="text-xs text-slate-500 mt-0.5">
            {version.uploadedByName ?? version.uploadedByEmail ?? "—"} ·{" "}
            {new Date(version.uploadedAt).toLocaleString()} · {files.length} file
            {files.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {files.length >= 2 && (
            <button
              type="button"
              className="text-xs text-ink hover:underline disabled:text-slate-400 disabled:no-underline"
              disabled={downloading}
              onClick={downloadAll}
              title={`Download all ${files.length} files as a zip`}
            >
              {downloading ? "zipping…" : "download all"}
            </button>
          )}
          {variant === "edit" && !renaming && (
            <button
              className="text-xs text-slate-400 hover:text-ink"
              onClick={() => {
                setRenameDraft(version.label);
                setRenaming(true);
              }}
            >
              rename
            </button>
          )}
        </div>
      </div>

      {files.length === 0 ? (
        <div className="text-xs text-slate-400 italic mt-2">
          {variant === "edit"
            ? dragOver
              ? "drop here"
              : "no files yet — drop here or click + Upload"
            : "no files"}
        </div>
      ) : (
        <ul className="mt-1 space-y-1">
          {files.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2 text-sm">
              <button
                className="truncate text-left text-ink hover:underline"
                title={`Open ${a.filename}`}
                onClick={async () => {
                  try {
                    const { url } = await api.getAttachmentUrl(a.id);
                    window.open(url, "_blank", "noopener,noreferrer");
                  } catch (e) {
                    window.alert(
                      `Couldn't open: ${e instanceof Error ? e.message : String(e)}`,
                    );
                  }
                }}
              >
                {a.filename}
              </button>
              {variant === "edit" && (
                <button
                  className="text-xs text-red-600 hover:underline shrink-0"
                  onClick={() => onDelete(a.id)}
                >
                  remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {variant === "edit" && (
        <div className="mt-2">
          <label className="text-xs text-ink hover:underline cursor-pointer">
            + Upload to this version
            <input
              type="file"
              multiple
              accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files ?? []);
                if (picked.length > 0) pickFiles(picked);
                e.target.value = "";
              }}
            />
          </label>
        </div>
      )}
    </li>
  );
}

/**
 * Renders any legacy attachments that have no versionId — i.e. uploads
 * from before the versioning model was introduced. Read-only display
 * with "remove" so the team can clean them up; new uploads go into
 * proper versions.
 */
function LegacyVersionCard({
  dealId,
  files,
  variant,
  onDelete,
}: {
  dealId: string;
  files: Attachment[];
  variant: "edit" | "read";
  onDelete: (id: string) => void;
}) {
  const [downloading, setDownloading] = useState(false);
  async function downloadAll() {
    setDownloading(true);
    try {
      // The legacy bucket is a mix of pre-PR-B deal_doc + revised_deal_doc
      // attachments. Filtering by a single kind here would silently drop
      // the other half (or 404 if the other half had everything). The
      // backend's onlyUnversionedDocs flag returns exactly what this
      // card displays.
      await api.downloadAttachmentsZip(dealId, { onlyUnversionedDocs: true });
    } catch (e) {
      window.alert(`Couldn't download: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setDownloading(false);
    }
  }
  return (
    <li className="rounded-md border border-amber-200 bg-amber-50/40 p-3">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div>
          <div className="font-medium text-sm">Legacy documents (no version)</div>
          <div className="text-xs text-amber-900/70 mt-0.5">
            Uploaded before document versioning existed. Run the admin migration to bucket
            these into proper versions, or remove them and re-upload into a new version.
          </div>
        </div>
        {files.length >= 2 && (
          <button
            type="button"
            className="text-xs text-ink hover:underline disabled:text-slate-400 disabled:no-underline shrink-0"
            disabled={downloading}
            onClick={downloadAll}
          >
            {downloading ? "zipping…" : "download all"}
          </button>
        )}
      </div>
      <ul className="mt-1 space-y-1">
        {files.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-2 text-sm">
            <button
              className="truncate text-left text-ink hover:underline"
              title={`Open ${a.filename}`}
              onClick={async () => {
                try {
                  const { url } = await api.getAttachmentUrl(a.id);
                  window.open(url, "_blank", "noopener,noreferrer");
                } catch (e) {
                  window.alert(
                    `Couldn't open: ${e instanceof Error ? e.message : String(e)}`,
                  );
                }
              }}
            >
              {a.filename}
              <span className="ml-1 text-xs text-amber-800/80">
                ({a.kind === "revised_deal_doc" ? "post-review" : "pre-review"})
              </span>
            </button>
            {variant === "edit" && (
              <button
                className="text-xs text-red-600 hover:underline shrink-0"
                onClick={() => onDelete(a.id)}
              >
                remove
              </button>
            )}
          </li>
        ))}
      </ul>
    </li>
  );
}

function FileButton({
  kind,
  label,
  onPick,
}: {
  kind: AttachmentKind;
  label: string;
  onPick: (files: File[], kind: AttachmentKind) => void;
}) {
  return (
    <label className="btn-secondary cursor-pointer">
      + {label}
      <input
        type="file"
        multiple
        accept=".docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          onPick(files, kind);
          e.target.value = "";
        }}
      />
    </label>
  );
}

function Spinner() {
  return (
    <svg className="animate-spin h-3 w-3 text-slate-400" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" opacity="0.25" />
      <path
        d="M4 12a8 8 0 018-8"
        stroke="currentColor"
        strokeWidth="4"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Group({
  title,
  kind,
  items,
  variant,
  dealId,
  onDelete,
  onDrop,
}: {
  title: string;
  kind: AttachmentKind;
  items: Attachment[];
  variant: "edit" | "read";
  dealId: string;
  onDelete: (id: string) => void;
  onDrop: (files: File[]) => void;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [downloading, setDownloading] = useState(false);

  async function open(a: Attachment) {
    try {
      const { url } = await api.getAttachmentUrl(a.id);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      window.alert(`Couldn't open: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  async function downloadAll() {
    setDownloading(true);
    try {
      await api.downloadAttachmentsZip(dealId, { kind });
    } catch (e) {
      window.alert(
        `Couldn't download: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setDownloading(false);
    }
  }

  const dropzoneProps =
    variant === "edit"
      ? {
          onDragEnter: (e: React.DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer?.types.includes("Files")) setDragOver(true);
          },
          onDragOver: (e: React.DragEvent) => {
            e.preventDefault();
            if (e.dataTransfer?.types.includes("Files")) setDragOver(true);
          },
          onDragLeave: (e: React.DragEvent) => {
            // Avoid flicker when moving into a child node
            if (e.currentTarget.contains(e.relatedTarget as Node)) return;
            setDragOver(false);
          },
          onDrop: (e: React.DragEvent) => {
            e.preventDefault();
            setDragOver(false);
            const files = Array.from(e.dataTransfer?.files ?? []);
            if (files.length > 0) onDrop(files);
          },
        }
      : {};

  return (
    <div
      {...dropzoneProps}
      className={`rounded-md transition ${
        dragOver ? "ring-2 ring-ink/50 bg-slate-50" : ""
      } ${variant === "edit" ? "p-1 -m-1" : ""}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs uppercase tracking-wide text-slate-400">{title}</div>
        {items.length >= 2 && (
          <button
            type="button"
            className="text-xs text-ink hover:underline disabled:text-slate-400 disabled:no-underline"
            disabled={downloading}
            onClick={downloadAll}
            title={`Download all ${items.length} files as a zip`}
          >
            {downloading ? "zipping…" : "download all"}
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-slate-400 text-xs italic">
          {variant === "edit" ? (dragOver ? "drop here" : "none") : "none"}
        </div>
      ) : (
        <ul className="mt-1 space-y-1">
          {items.map((a) => (
            <li key={a.id} className="flex items-center justify-between gap-2">
              <button
                className="truncate text-left text-ink hover:underline"
                title={`Open ${a.filename}`}
                onClick={() => open(a)}
              >
                {a.filename}
              </button>
              {variant === "edit" && (
                <button
                  className="text-xs text-red-600 hover:underline shrink-0"
                  onClick={() => onDelete(a.id)}
                >
                  remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DeleteDealModal({
  deal,
  onClose,
  onDeleted,
}: {
  deal: Deal;
  onClose: () => void;
  onDeleted: () => void;
}) {
  // Type-to-confirm is the only friction in the way of a permanent
  // destructive op. We require the user to type the deal title
  // verbatim so a single misclick can't wipe a deal.
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const matches = typed.trim() === deal.title.trim();

  async function submit() {
    if (!matches) return;
    setBusy(true);
    setErr(null);
    try {
      await api.deleteDeal(deal.id);
      onDeleted();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-40">
      <div className="card w-full max-w-md p-6">
        <h2 className="font-semibold text-lg mb-2 text-red-700">Delete deal</h2>
        <p className="text-sm text-slate-700 mb-3">
          This permanently deletes <strong>{deal.title}</strong> and{" "}
          <em>everything</em> under it: every review, every checklist item, every chat
          message, every uploaded file (both the file itself and its extracted text),
          every document version, and every external share. <strong>No undo.</strong>
        </p>
        <p className="text-sm text-slate-600 mb-3">
          Type the deal title <span className="font-mono">{deal.title}</span> below to
          confirm.
        </p>
        <input
          className="input mb-3 font-mono"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder={deal.title}
          autoFocus
        />
        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}
        <div className="flex justify-end gap-2">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!matches || busy}
            onClick={submit}
          >
            {busy ? "Deleting…" : "Permanently delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewReviewModal({
  dealId,
  versions,
  attachments,
  onClose,
  onCreated,
}: {
  dealId: string;
  versions: DocumentVersion[];
  attachments: Attachment[];
  onClose: () => void;
  onCreated: (r: Review) => void;
}) {
  const sortedVersions = useMemo(
    () => [...versions].sort((a, b) => a.order - b.order),
    [versions],
  );
  const latest = sortedVersions[sortedVersions.length - 1];

  const [title, setTitle] = useState("");
  const [reviewType, setReviewType] = useState<ReviewType>("document_review");
  const [versionId, setVersionId] = useState<string>(latest?.id ?? "");
  // For Redline Review: default the prior version to whichever one
  // immediately precedes the selected version.
  const [priorVersionId, setPriorVersionId] = useState<string>(() => {
    if (sortedVersions.length < 2) return "";
    return sortedVersions[sortedVersions.length - 2]!.id;
  });
  // Scope: everything on by default; skipping is opt-out. Stored as the
  // full skip set across types and filtered to the chosen type on submit,
  // so switching type and back doesn't lose the user's picks.
  const [skip, setSkip] = useState<ReadonlySet<ReviewSectionKey>>(new Set());
  const [quickPass, setQuickPass] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const { user } = useAuth();
  const isAdmin = useAuth().isAdmin === true;

  // When the user changes the selected version, re-default the prior
  // version to the one immediately before it (unless they've already
  // explicitly picked something else and it's still a valid choice).
  useEffect(() => {
    if (reviewType !== "redline_review") return;
    const idx = sortedVersions.findIndex((v) => v.id === versionId);
    if (idx <= 0) {
      setPriorVersionId("");
      return;
    }
    const candidate = sortedVersions[idx - 1]!.id;
    // Only auto-update if the current prior choice isn't a valid older
    // version (i.e. it's empty or it's the same/after selected).
    const priorIdx = sortedVersions.findIndex((v) => v.id === priorVersionId);
    if (priorIdx < 0 || priorIdx >= idx) {
      setPriorVersionId(candidate);
    }
  }, [versionId, reviewType, sortedVersions, priorVersionId]);

  const noVersions = sortedVersions.length === 0;
  const needsPriorVersion = reviewType === "redline_review";

  const skippable = SKIPPABLE_SECTIONS[reviewType];
  // The redline prompt has no quick format, so the preset is hidden there.
  const presets = REVIEW_SCOPE_PRESETS.filter((p) => !(p.quickPass && needsPriorVersion));
  const effectiveQuick = quickPass && !needsPriorVersion;
  const effectiveSkip = effectiveQuick ? [] : skippable.filter((s) => skip.has(s));
  const presetSkip = (p: ReviewScopePreset) => skippable.filter((s) => p.skip.includes(s));
  const activePreset = presets.find((p) =>
    p.quickPass
      ? effectiveQuick
      : !effectiveQuick &&
        presetSkip(p).length === effectiveSkip.length &&
        presetSkip(p).every((s) => skip.has(s)),
  )?.key;

  // Redline reviews work dramatically better when the current version's
  // Word docs carry tracked changes (extraction inlines them for Claude);
  // without markup Claude must infer the diff itself, which can miss small
  // numeric/definition edits. Tell the user which mode they're about to get.
  const currentVersionDocs = attachments.filter((a) => a.versionId === versionId);
  const currentDocx = currentVersionDocs.filter(
    (a) =>
      a.mimeType ===
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  );
  const currentPdfCount = currentVersionDocs.length - currentDocx.length;
  const markerDocxCount = currentDocx.filter((a) => a.hasTrackedChanges).length;
  // Green only when EVERY file in the version carries tracked changes —
  // a mixed package (clean docx or PDFs alongside a redlined doc) still
  // reviews the uncovered files via the weaker side-by-side inference,
  // and the user must see that.
  const redlineSignal: "markers" | "partial" | "clean_docx" | "no_docx" | null =
    !needsPriorVersion
      ? null
      : currentDocx.length === 0
        ? "no_docx"
        : markerDocxCount === currentDocx.length && currentPdfCount === 0
          ? "markers"
          : markerDocxCount > 0
            ? "partial"
            : "clean_docx";

  const canSubmit =
    !busy &&
    !noVersions &&
    versionId.length > 0 &&
    (!needsPriorVersion || (priorVersionId.length > 0 && priorVersionId !== versionId));

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center p-4">
      <div className="card w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="font-semibold text-lg mb-4">New review</h2>

        <label className="block text-sm font-medium mb-1">
          Title <span className="font-normal text-slate-400">(optional)</span>
        </label>
        <input
          className="input mb-3"
          placeholder="auto: Review N"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />

        <label className="block text-sm font-medium mb-1">Review type</label>
        <div className="space-y-2 mb-3">
          {REVIEW_TYPES.map((t) => (
            <label
              key={t.value}
              className={`flex gap-2 p-2 rounded border cursor-pointer transition ${
                reviewType === t.value
                  ? "border-ink bg-slate-50"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              <input
                type="radio"
                name="reviewType"
                value={t.value}
                checked={reviewType === t.value}
                onChange={() => setReviewType(t.value)}
                className="mt-0.5"
              />
              <div className="min-w-0">
                <div className="font-medium text-sm">{t.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>
              </div>
            </label>
          ))}
        </div>

        {needsPriorVersion && (
          <div className="rounded border border-slate-200 bg-slate-50 p-2.5 mb-3 text-xs text-slate-600">
            <span className="font-medium text-slate-700">How redline review works:</span>{" "}
            pick the version that came back from the counterparty (ideally their redlined
            Word docs — Claude reads every tracked change directly, with author) and the
            prior version we sent. Both go to Claude: the redline shows what they{" "}
            <em>declared</em> changed, and the prior version lets Claude catch anything
            edited with track-changes off.
          </div>
        )}

        <label className="block text-sm font-medium mb-1">
          {needsPriorVersion ? (
            <>
              Current version — what came back <span className="text-red-600">*</span>
            </>
          ) : (
            <>
              Document version <span className="text-red-600">*</span>
            </>
          )}
        </label>
        {noVersions ? (
          <div className="input mb-3 bg-amber-50 border-amber-200 text-sm text-amber-900">
            No document versions yet. Create one in the Documents panel before starting
            a review.
          </div>
        ) : (
          <select
            className="input mb-3"
            value={versionId}
            onChange={(e) => setVersionId(e.target.value)}
          >
            {sortedVersions.map((v) => (
              <option key={v.id} value={v.id}>
                v{v.order} — {v.label}
              </option>
            ))}
          </select>
        )}

        {redlineSignal === "markers" && !noVersions && (
          <div className="rounded border border-green-200 bg-green-50 p-2 -mt-1 mb-3 text-xs text-green-900">
            ✓ Tracked changes detected in all {currentDocx.length} Word doc
            {currentDocx.length === 1 ? "" : "s"} — Claude will read each
            insertion/deletion directly.
          </div>
        )}
        {redlineSignal === "partial" && !noVersions && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 -mt-1 mb-3 text-xs text-amber-900">
            Tracked changes detected in {markerDocxCount} of {currentDocx.length} Word doc
            {currentDocx.length === 1 ? "" : "s"}
            {currentPdfCount > 0
              ? `, plus ${currentPdfCount} PDF${currentPdfCount === 1 ? "" : "s"}`
              : ""}
            . The files without markup will be reviewed by comparing the two versions —
            small edits there can slip through. Consider{" "}
            <span className="font-medium">⇄ Create redlines</span> for the clean Word docs
            {currentPdfCount > 0 ? " and uploading Word versions of the PDFs" : ""}.
          </div>
        )}
        {redlineSignal === "clean_docx" && !noVersions && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 -mt-1 mb-3 text-xs text-amber-900">
            No tracked changes detected in this version's Word docs. Claude will infer the
            changes by comparing the two versions — reliable for big edits, but small
            numeric or defined-term changes can slip through. For best results, close this,
            use <span className="font-medium">⇄ Create redlines</span> in the Documents
            panel to generate redlines from these two versions, then review the generated
            version instead.
          </div>
        )}
        {redlineSignal === "no_docx" && !noVersions && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 -mt-1 mb-3 text-xs text-amber-900">
            This version has no Word documents. PDFs can't carry tracked changes, so Claude
            will compare the two versions by reading them side by side — small edits may be
            missed. If Word versions exist, upload those instead.
          </div>
        )}

        {needsPriorVersion && !noVersions && (
          <>
            <label className="block text-sm font-medium mb-1">
              Prior version — what we sent <span className="text-red-600">*</span>
            </label>
            {(() => {
              // Only versions strictly older than the selected current
              // qualify as "prior" — picking a newer version would invert
              // the diff at the prompt level. Backend rejects it too;
              // this just keeps the dropdown from offering wrong choices.
              const currentOrder = sortedVersions.find((v) => v.id === versionId)?.order;
              const olderVersions = sortedVersions.filter(
                (v) =>
                  v.id !== versionId &&
                  currentOrder !== undefined &&
                  v.order < currentOrder,
              );
              if (olderVersions.length === 0) {
                return (
                  <div className="input mb-3 bg-amber-50 border-amber-200 text-sm text-amber-900">
                    No older version exists to diff against. Either pick a later
                    "Document version" above, or create the prior version first.
                  </div>
                );
              }
              return (
                <select
                  className="input mb-3"
                  value={priorVersionId}
                  onChange={(e) => setPriorVersionId(e.target.value)}
                >
                  <option value="">— pick a prior version —</option>
                  {olderVersions.map((v) => (
                    <option key={v.id} value={v.id}>
                      v{v.order} — {v.label}
                    </option>
                  ))}
                </select>
              );
            })()}
          </>
        )}

        <label className="block text-sm font-medium mb-1">Scope</label>
        <div className="flex flex-wrap gap-1.5 mb-2">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              title={p.description}
              onClick={() => {
                setQuickPass(p.quickPass === true);
                setSkip(new Set(p.skip));
              }}
              className={`px-2.5 py-1 rounded border text-xs transition ${
                activePreset === p.key
                  ? "border-ink bg-slate-50 font-medium"
                  : "border-slate-200 hover:border-slate-400"
              }`}
            >
              {p.label}
            </button>
          ))}
          {!activePreset && (
            <span className="px-2.5 py-1 rounded border border-ink bg-slate-50 text-xs font-medium">
              Custom
            </span>
          )}
        </div>
        {effectiveQuick ? (
          <div className="rounded border border-slate-200 bg-slate-50 p-2.5 mb-3 text-xs text-slate-600">
            {reviewType === "standalone_review"
              ? "Quick pass: the term sheet comparison (only if a term sheet or intake sheet is attached) limited to economic, security and default terms, orphan defined terms on operative provisions, and High-priority items only."
              : "Quick pass: the comparison table limited to economic, security and default terms, plus High-priority items only."}
          </div>
        ) : (
          <div className="rounded border border-slate-200 p-2.5 mb-3 space-y-1.5">
            {skippable.map((s) => (
              <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!skip.has(s)}
                  onChange={(e) => {
                    const next = new Set(skip);
                    if (e.target.checked) next.delete(s);
                    else next.add(s);
                    setSkip(next);
                  }}
                />
                {REVIEW_SECTION_LABEL[s]}
              </label>
            ))}
            <div className="text-xs text-slate-500 pt-1">
              {needsPriorVersion
                ? "Summary of Changes and Weakened Positions always run."
                : reviewType === "standalone_review"
                  ? "The summary always runs; the term sheet comparison runs when a term sheet or intake sheet is attached."
                  : "The term sheet comparison and the summary always run."}
              {effectiveSkip.length > 0 &&
                " Skipped sections are noted on the review so teammates can see what wasn't run."}
            </div>
          </div>
        )}

        {err && <div className="text-sm text-red-600 mb-3">{err}</div>}

        <div className="flex justify-end gap-2 mt-4">
          <button className="btn-secondary" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!canSubmit}
            onClick={async () => {
              setBusy(true);
              setErr(null);
              try {
                const { review } = await api.createReview(dealId, {
                  title: title.trim() || undefined,
                  reviewType,
                  versionId,
                  ...(needsPriorVersion ? { priorVersionId } : {}),
                  ...(effectiveQuick ? { quickPass: true } : {}),
                  ...(effectiveSkip.length > 0 ? { skipSections: effectiveSkip } : {}),
                });
                onCreated(review);
              } catch (e) {
                setErr(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}
